import { createContext, useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { typographyRecipes } from "../../../design/recipes/typographyRecipes";
import { FILTER_DROPDOWN_GAP, FILTER_ROW_ITEM_SX } from "../rewampFilterLayout";
import { useRewampT } from "../../../localisation";
import { RewampFieldHelperText } from "../../schemeApply/formEngine/widgets/RewampGridFieldStack";

// labelPlacement="inline": label <gap-1> control on one line, control fills the cell height
// (filterDropDown-recipe.md).
const INLINE_CELL_SX = { display: "flex", alignItems: "center", gap: 1 };
const INLINE_LABEL_SX = { color: "text.primary", whiteSpace: "nowrap", flexShrink: 0 };
const FILTER_HELPER_SPACE = 2.5;
const HELPER_SLOT_SX = { position: "absolute", top: "100%", left: 0, mt: 0.5, whiteSpace: "nowrap" };

// Dropdown windows anchor to the whole [label – control] cell (full width), `gap` below it.
const FilterCellAnchorContext = createContext({ anchorEl: null, gap: FILTER_DROPDOWN_GAP });
export const useFilterCellAnchor = () => useContext(FilterCellAnchorContext);

export default function FilterFieldCell({ labelKey, label, controlId, children, itemSx, labelPlacement, dropdownGap, helperText, helperId }) {
  const tRewamp = useRewampT();
  const resolvedLabel = label || (labelKey ? tRewamp(labelKey) : null);
  const inline = labelPlacement === "inline";
  const [cellEl, setCellEl] = useState(null);
  const anchor = useMemo(() => ({ anchorEl: cellEl, gap: dropdownGap }), [cellEl, dropdownGap]);

  // Helper text sits in reserved space under the control, so showing it never remounts the control.
  const helperSx = helperText ? { mb: FILTER_HELPER_SPACE } : null;
  const controlSx = { position: "relative", ...(inline ? { flex: 1, minWidth: 0 } : null) };

  return (
    <Box ref={setCellEl} sx={{ ...FILTER_ROW_ITEM_SX, ...(inline ? INLINE_CELL_SX : null), ...helperSx, ...itemSx }}>
      {resolvedLabel && (
        <Typography
          component="label"
          htmlFor={controlId}
          variant={inline ? "filterLabel" : "body2"}
          sx={{ ...(inline ? INLINE_LABEL_SX : typographyRecipes.filterLabel), ...(helperText ? { color: "formMessage.error" } : null) }}
        >
          {resolvedLabel}
        </Typography>
      )}
      <FilterCellAnchorContext.Provider value={anchor}>
        <Box sx={controlSx}>
          {children}
          {helperText && (
            <Box role="alert" sx={HELPER_SLOT_SX}>
              <RewampFieldHelperText id={helperId} message={helperText} />
            </Box>
          )}
        </Box>
      </FilterCellAnchorContext.Provider>
    </Box>
  );
}

FilterFieldCell.propTypes = {
  labelKey: PropTypes.string,
  label: PropTypes.string,
  controlId: PropTypes.string,
  children: PropTypes.node.isRequired,
  itemSx: PropTypes.object,
  labelPlacement: PropTypes.oneOf(["above", "inline"]),
  dropdownGap: PropTypes.number,
  helperText: PropTypes.string,
  helperId: PropTypes.string,
};

FilterFieldCell.defaultProps = {
  labelKey: null,
  label: null,
  controlId: undefined,
  itemSx: {},
  labelPlacement: "above",
  dropdownGap: FILTER_DROPDOWN_GAP,
  helperText: null,
  helperId: undefined,
};
